/**
 * API Client Configuration
 *
 * Centralized configuration for API client with security enhancements.
 */

import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { getApiUrl, getRuntimeMode, log } from "../config/env";
import {
  applyCsrfRequestHeader,
  captureCsrfResponseHeader,
  clearCachedCsrfToken,
  ensureCsrfToken,
} from "../utils/csrfToken";
import { createTokenExpirationInterceptor } from "../utils/secureTokenHandler";
import { secureTokenManager } from "../utils/secureTokenManager";
import { secureErrorHandler } from "../utils/secureErrorHandler";
import { getSessionId } from "../lib/sessionGet";
import { v4 as uuidv4 } from "uuid";
import { getSafeRequestPath } from "../utils/requestLog";

// API Configuration
export const API_CONFIG = {
  BASE_URL: getApiUrl(),
  TIMEOUT: 60000, // Increased to 60 seconds for complex searches
};

// Axios instance with security enhancements
export const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  withCredentials: true, // Enable credentials for httpOnly cookie support
});

// Add request interceptor
// SECURITY: Tokens are handled via httpOnly cookies (set by backend)
// Cookies are automatically sent with requests when withCredentials: true
// Only add Authorization header as fallback for backward compatibility
// CORRELATION: Session ID is added to all requests for tracing frontend → backend events
apiClient.interceptors.request.use(
  (config) => {
    // Re-resolve in the browser so Vite dev uses /api/v1 proxy (not build-time localhost:8000).
    if (typeof window !== "undefined" && getRuntimeMode() === "development") {
      config.baseURL = getApiUrl();
    }

    // Call sites that historically used config/api (origin baseURL) pass absolute
    // `/api/v1/...` paths. When baseURL already ends with `/api/v1`, strip the
    // duplicate prefix so both conventions resolve to one authenticated client.
    const baseURL = String(config.baseURL ?? API_CONFIG.BASE_URL ?? "");
    const url = config.url;
    if (
      typeof url === "string" &&
      baseURL.replace(/\/$/, "").endsWith("/api/v1")
    ) {
      if (url === "/api/v1" || url.startsWith("/api/v1?")) {
        config.url = url.replace("/api/v1", "") || "/";
      } else if (url.startsWith("/api/v1/")) {
        config.url = url.slice("/api/v1".length);
      }
    }

    // Default Content-Type is application/json. For FormData, axios would otherwise
    // JSON-serialize the payload (see axios transformRequest), breaking multipart uploads
    // and causing FastAPI File(...) to see "file: Field required".
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      const h = config.headers as {
        delete?: (name: string) => void;
      } & Record<string, unknown>;
      if (typeof h.delete === "function") {
        h.delete("Content-Type");
      } else {
        delete h["Content-Type"];
      }
    }

    // Add session ID for correlation between frontend and backend events
    config.headers["X-Session-ID"] = getSessionId();
    // Per-request correlation ID for tracing a single request frontend → backend
    config.headers["X-Correlation-ID"] = uuidv4();

    // httpOnly cookies are automatically sent by browser when withCredentials: true
    // Only add Authorization header if token exists in secureTokenManager (for migration/fallback)
    // In production with cookie-only mode, this will be null and cookies will be used
    const token = secureTokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // RBAC Phase 4: attach active org/facility for Layer 2 mutation gates.
    // Lazy require avoids circular imports with auth/org stores.
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { useOrgContextStore } = require("../stores/orgContextStore") as {
        useOrgContextStore: {
          getState: () => {
            activeOrganizationId: number | null;
            activeProviderId: number | null;
          };
        };
      };
      const orgCtx = useOrgContextStore.getState();
      if (orgCtx.activeOrganizationId != null) {
        config.headers["X-Organization-Id"] = String(
          orgCtx.activeOrganizationId
        );
      }
      if (orgCtx.activeProviderId != null) {
        config.headers["X-Provider-Id"] = String(orgCtx.activeProviderId);
      }
    } catch {
      // Store not available yet — request continues without org headers
    }

    applyCsrfRequestHeader(config);
    // Note: Even if token is null, httpOnly cookies are sent automatically by browser
    return config;
  },

  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
// The interceptor will use dependency injection to break circular dependency
const handleTokenExpiration = createTokenExpirationInterceptor({
  redirectToLogin: true,
  showNotification: true,
  axiosInstance: apiClient,
  // Provide getAuthState callback that lazy loads authStore
  getAuthState: () => {
    try {
      // Lazy import to break circular dependency
      // This will only work if authStore is already loaded
      const authStoreModule = require("../stores/authStore");
      return authStoreModule.useAuthStore?.getState() || null;
    } catch {
      // Auth store not available - return null (interceptor will handle gracefully)
      return null;
    }
  },
});

type CsrfRetriableConfig = InternalAxiosRequestConfig & {
  _csrfRetry?: boolean;
};

function isCsrfForbiddenError(error: AxiosError<{ detail?: string }>): boolean {
  if (error.response?.status !== 403) {
    return false;
  }
  const detail = error.response.data?.detail;
  return typeof detail === "string" && detail.includes("CSRF");
}

apiClient.interceptors.response.use(
  (response) => {
    captureCsrfResponseHeader(
      response.headers as unknown as Record<string, unknown>
    );
    return response;
  },
  async (error: AxiosError<{ detail?: string }>) => {
    const config = error.config as CsrfRetriableConfig | undefined;
    if (config && isCsrfForbiddenError(error) && !config._csrfRetry) {
      config._csrfRetry = true;
      clearCachedCsrfToken();
      try {
        await ensureCsrfToken({ force: true });
      } catch {
        // Fall through to normal error handling if bootstrap fails.
        return handleTokenExpiration(error);
      }
      applyCsrfRequestHeader(config);
      return apiClient.request(config);
    }
    return handleTokenExpiration(error);
  }
);

// Error handling utility with secure error sanitization
export const handleAPIError = (error: unknown): string => {
  const sanitizedError = secureErrorHandler.handleApiError(error, "API");
  return sanitizedError.message;
};

// Request deduplication utility
const requestCache = new Map<string, Promise<unknown>>();

export const deduplicateRequest = <T>(
  key: string,
  requestFn: () => Promise<T>
): Promise<T> => {
  const cached = requestCache.get(key);
  if (cached) {
    return cached as Promise<T>;
  }

  const promise = requestFn().finally(() => {
    requestCache.delete(key);
  });

  requestCache.set(key, promise);
  return promise;
};

// Rate limiting utility
const rateLimitMap = new Map<string, { count: number; resetTime: number }>();

export const checkRateLimit = (
  key: string,
  maxRequests: number = 100,
  windowMs: number = 60000
): boolean => {
  const now = Date.now();
  const limit = rateLimitMap.get(key);

  if (!limit || now > limit.resetTime) {
    rateLimitMap.set(key, { count: 1, resetTime: now + windowMs });
    return true;
  }

  if (limit.count >= maxRequests) {
    return false;
  }

  limit.count++;
  return true;
};

// Request logging utility
export const logRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  const method = config.method?.toUpperCase() || "UNKNOWN";
  log("info", `API Request: ${method} ${getSafeRequestPath(config.url)}`);
  return config;
};

// Response logging utility
export const logResponse = (response: AxiosResponse): AxiosResponse => {
  const status = response.status || 0;
  log(
    "info",
    `API Response: ${status} ${getSafeRequestPath(response.config?.url)}`
  );
  return response;
};

apiClient.interceptors.request.use(logRequest, (error) =>
  Promise.reject(error)
);

apiClient.interceptors.response.use(logResponse, (error) =>
  Promise.reject(error)
);
